import {Metadata} from "next";
import {PageService, PostService, ProductService} from "@api";

export const getSiteUrl = (): string => ((process.env.SITE_URL || process.env.CF_PAGES_URL || 'https://active710.com').replace(/\/$/, ""));

/**
 * Same as getSiteUrl but falls back to the local dev origin, so the RSS
 * <link> and share images stay resolvable when running `next dev`.
 */
export const getFeedSiteUrl = (): string =>
    (process.env.SITE_URL || process.env.CF_PAGES_URL || 'http://localhost:3002').replace(/\/$/, '');

export enum MetadataObjectType {
    Page = 'page',
    Product = 'product',
    Article = 'article'
}

export interface MetadataData {
    title: string;
    description: string;
    url: string;
    datePublished?: string;
    dateModified?: string;
}

const toIsoOrUndefined = (value: unknown): string | undefined => {
    if (typeof value !== "string" || value.length === 0) return undefined;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return undefined;
    return d.toISOString();
};

const HOME_PAGE_SLUG = 'home-page';

const DEFAULT_TITLE = 'Cannabis Vape Cartridge Manufacturer | ACTIVE® Vapor Devices';

/**
 * The `icons` list every page built through this file carries.
 *
 * Both builders below set `icons` WHOLE, and Next replaces that field rather
 * than deep-merging it — so whatever the root layout puts there is dropped on
 * every page that goes through here, which is most of the site. That is why the
 * theme-aware favicon has to be repeated in this list rather than declared once
 * in app/layout.tsx: without it only the handful of pages with their own
 * metadata kept the link, and everywhere else fell back to the .ico alone.
 */
const SITE_ICONS = [
    // Adapts to light and dark; see app/icon.svg. favicon.ico stays as the
    // fallback for Safari and anything without SVG icon support.
    {rel: 'icon', url: '/icon.svg', type: 'image/svg+xml'},
    {rel: 'preconnect', url: 'https://images.active710.com'},
    {rel: 'preconnect', url: 'https://images2.active710.com'},
    {rel: 'preconnect', url: 'https://images3.active710.com'},
    {rel: 'preconnect', url: 'https://images4.active710.com'},
];

export const getEuropeHreflangAlternates = (): Record<string, string> => {
    const siteUrl = getSiteUrl();
    return {
        'en-US': `${siteUrl}/`,
        'en-GB': `${siteUrl}/europe`,
        'x-default': `${siteUrl}/`,
    };
};

const logFallback = (message: string): void => {
    console.warn(message);
    if (typeof process !== 'undefined' && process.stderr?.write) {
        process.stderr.write(`${message}\n`);
    }
};

export const buildUrl = (permalink: string | null | undefined, customLink: string | null | undefined): string => {
    const raw = (customLink ?? permalink ?? "").replace(/^\//, "");
    return raw ? `${getSiteUrl()}/${raw}` : getSiteUrl();
};

const getPageMetadata = async (slug: string, customLink?: string): Promise<MetadataData | null> => {
    const pageData = await PageService.getBySlug(slug);
    if (!pageData) return null;
    const permalink = (customLink ?? pageData.slug ?? "").replace(/^\//, "");
    const title = pageData.seoTitle || pageData.title || '';
    let description = pageData.seoDescription || '';
    if (!description.trim()) {
        logFallback(`[getPageMetadata] Missing seoDescription for page "${slug}" — using fallback. Please populate the CMS.`);
        description = `${title} — ACTIVE® Vapor Devices. Cannabis vape hardware trusted by 400+ brands.`;
    }
    return {
        title,
        description,
        url: pageData.slug === HOME_PAGE_SLUG ? getSiteUrl() : `${getSiteUrl()}/${permalink}`,
        datePublished: toIsoOrUndefined(pageData.createdAt),
        dateModified: toIsoOrUndefined(pageData.updatedAt),
    };
};

const getProductMetadata = async (slug: string, customLink?: string): Promise<MetadataData | null> => {
    const productData = await ProductService.getBySlug(slug);
    if (!productData) return null;
    const title = productData.seoTitle || productData.title || '';
    let description = productData.seoDescription || '';
    if (!description.trim()) {
        logFallback(`[getProductMetadata] Missing seoDescription for product "${slug}" — using fallback. Please populate the CMS.`);
        description = `${productData.title} — cannabis vape hardware by ACTIVE®. Request samples, customization, and filling & capping support.`;
    }
    return {
        title,
        description,
        url: buildUrl(productData.permalink, customLink),
        datePublished: toIsoOrUndefined(productData.createdAt),
        dateModified: toIsoOrUndefined(productData.updatedAt),
    };
};

const getArticleMetadata = async (slug: string, customLink?: string): Promise<MetadataData | null> => {
    const post = await PostService.getBySlug(slug);
    if (!post) return null;
    const permalink = (customLink ?? `news/${post.slug ?? ""}`).replace(/^\//, "");
    const stripped = (post.content ?? '').replace(/(<([^>]+)>)/ig, '').trim();
    let description = stripped.substring(0, 160);
    if (!description) {
        logFallback(`[getArticleMetadata] Missing content for post "${slug}" — using fallback. Please populate the CMS.`);
        description = `${post.title} — News from ACTIVE® Vapor Devices.`;
    }
    return {
        title: `${post.title} | News | ACTIVE`,
        description,
        url: `${getSiteUrl()}/${permalink}`,
        datePublished: toIsoOrUndefined(post.publishDate ?? post.createdAt),
        dateModified: toIsoOrUndefined(post.updatedAt),
    };
};

export const getMetadataData = async (slug: string, objectType: MetadataObjectType = MetadataObjectType.Page, customLink?: string): Promise<MetadataData> => {
    let data: MetadataData | null = null;
    switch (objectType) {
        case MetadataObjectType.Page:
            data = await getPageMetadata(slug, customLink);
            break;
        case MetadataObjectType.Product:
            data = await getProductMetadata(slug, customLink);
            break;
        case MetadataObjectType.Article:
            data = await getArticleMetadata(slug, customLink);
            break;
    }

    if (data) return data;

    logFallback(`[getMetadataData] No ${objectType} found for slug "${slug}" — using default metadata fallback. Please populate the CMS.`);
    const siteUrl = getSiteUrl();
    const fallbackPath = (customLink ?? slug).replace(/^\//, "");
    return {
        title: DEFAULT_TITLE,
        description: 'ACTIVE® Vapor Devices. Cannabis vape hardware trusted by 400+ brands.',
        url: fallbackPath ? `${siteUrl}/${fallbackPath}` : siteUrl,
    };
};


export interface MetadataAuthor {
    name: string;
    url?: string;
}

const DEFAULT_EDITORIAL_AUTHOR_NAME = "ACTIVE Editorial";

const resolveAuthors = (authors: MetadataAuthor[] | undefined): MetadataAuthor[] => {
    if (authors && authors.length > 0) return authors;
    const siteUrl = getSiteUrl();
    return [{name: DEFAULT_EDITORIAL_AUTHOR_NAME, url: `${siteUrl}/about-active-vapor-devices`}];
};

const toAbsolute = (siteUrl: string, url: string | undefined): string | undefined => {
    if (!url) return undefined;
    if (/^https?:\/\//i.test(url)) return url;
    return `${siteUrl}${url.startsWith("/") ? "" : "/"}${url}`;
};

export interface StaticMetadataOptions {
    noindex?: boolean;
    imageLink?: string;
    authors?: MetadataAuthor[];
}

export const buildStaticMetadata = (
    title: string,
    description: string,
    path: string,
    opts: StaticMetadataOptions = {}
): Metadata => {
    const siteUrl = getSiteUrl();
    const url = path ? `${siteUrl}/${path.replace(/^\//, "")}` : siteUrl;
    const imageURL = `${siteUrl}/images/open-graph/${opts.imageLink || 'active-share-hero.2.png'}`;
    const authors = resolveAuthors(opts.authors).map((a) => ({
        name: a.name,
        url: toAbsolute(siteUrl, a.url),
    }));

    return {
        title,
        description,
        authors,
        alternates: {canonical: url},
        openGraph: {
            type: 'website',
            url,
            title,
            description,
            images: [{url: imageURL, width: 2400, height: 1256, alt: title}],
        },
        twitter: {
            card: 'summary_large_image',
            site: '@avd710',
            creator: '@avd710',
            title,
            description,
            images: [imageURL],
        },
        icons: SITE_ICONS,
        robots: opts.noindex ? 'noindex, follow, max-image-preview:large' : 'max-image-preview:large',
    };
};


export interface BuildMetadataOptions {
    noindex?: boolean;
    authors?: MetadataAuthor[];
}

export const buildMetadata = async (slug: string, objectType: MetadataObjectType = MetadataObjectType.Page, customLink?: string, imageLink?: string, hreflangAlternates?: Record<string, string>, opts?: BuildMetadataOptions): Promise<Metadata> => {
    const data = await getMetadataData(slug, objectType, customLink);
    const siteUrl = getSiteUrl();
    const imageURL = `${siteUrl}/images/open-graph/${imageLink || 'active-share-hero.2.png'}`;
    const authors = resolveAuthors(opts?.authors).map((a) => ({
        name: a.name,
        url: toAbsolute(siteUrl, a.url),
    }));

    const otherMeta: Record<string, string | string[]> = {};
    if (data.datePublished) otherMeta["article:published_time"] = data.datePublished;
    if (data.dateModified) otherMeta["article:modified_time"] = data.dateModified;

    return {
        title: data.title,
        description: data.description,
        authors,
        alternates: {
            canonical: data.url,
            ...(hreflangAlternates ? {languages: hreflangAlternates} : {}),
        },
        openGraph: {
            type: 'website',
            url: data.url,
            title: data.title,
            description: data.description,
            images: [
                {
                    url: imageURL,
                    width: 2400,
                    height: 1256,
                    alt: data.title,
                },
            ],
        },
        twitter: {
            card: 'summary_large_image',
            site: '@avd710',
            creator: '@avd710',
            title: data.title,
            description: data.description,
            images: [imageURL],
        },
        icons: SITE_ICONS,
        robots: opts?.noindex ? 'noindex, follow, max-image-preview:large' : 'max-image-preview:large',
        ...(Object.keys(otherMeta).length > 0 ? {other: otherMeta} : {}),
    }
}
